import { useDispatch } from 'react-redux';
import { useCallback, useEffect, useRef } from 'react';
import { useLocation, useNavigationType } from 'react-router';

import { putSupportProviderSetForClientByClientIdRequest } from '@client/model';

export function useSupportProviderSyncOnExit(clientSupportProviderSet: SupportProviderSet | null) {
  const dispatch = useDispatch();
  const location = useLocation();
  const navigationType = useNavigationType();

  const latestRef = useRef(clientSupportProviderSet);
  latestRef.current = clientSupportProviderSet;

  const sentRef = useRef(false);

  const updateClientSupportProviderSet = useCallback(
    (value: SupportProviderSet | null) => {
      if (value) {
        dispatch(
          putSupportProviderSetForClientByClientIdRequest({
            supportProviderSet: value,
          }),
        );
      }
    },
    [dispatch],
  );

  const update = useCallback(() => {
    if (sentRef.current) return;
    const data = latestRef.current;
    if (!data) return;

    sentRef.current = true;
    updateClientSupportProviderSet(data);
  }, [updateClientSupportProviderSet]);

  useEffect(() => {
    if (navigationType === 'POP') {
      update();
    }
  }, [location, navigationType, update]);

  useEffect(() => {
    const onPageHide = () => update();
    const onBeforeUnload = () => update();

    window.addEventListener('pagehide', onPageHide);
    window.addEventListener('beforeunload', onBeforeUnload);

    return () => {
      window.removeEventListener('pagehide', onPageHide);
      window.removeEventListener('beforeunload', onBeforeUnload);
    };
  }, [update]);

  return { updateClientSupportProviderSet };
}
